import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { UtilityService } from 'src/app/services/utility.service';
import { AuthService } from 'src/app/services/auth.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { ChartConfiguration, ChartData, ChartEvent, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import moment from 'moment';
import { LocalizationService } from 'src/app/services/localization.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { LoggerService } from '../../services/logger.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit, OnDestroy {

  constructor(
    private _utilityService: UtilityService,
    public _authService: AuthService,
    private drupalRESTService: DrupalRESTService,
    public _permissionsService: PermissionsService,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
    private localizationService: LocalizationService,
    private studioConfigService: StudioConfigService
  ) {
    this.studioConfigSubscription = this.studioConfigService.getStudioConfig().subscribe(config => {
      const countryCode = config?.['field_studio_address']?.[0]?.country_code || 'US';
      const formats = this.localizationService.getDateFormatsForCountry(countryCode);
      this.currentDateFormat = formats.calendarDateFormat.startsWith('DD') ? 'dddd, D MMMM YYYY' : 'dddd, MMMM D, YYYY';
    });
  }

  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;
  dashboardData: any;
  chart_data: any;
  private studioChangeSubscription: Subscription;
  appVersion: string = '1.10533';

  // Example of using translation in TypeScript
  pageTitle: string = '';

  // Dashboard metrics from API
  metrics: any = {
    active_students: {
      value: 0,
      label: 'Active Student Accounts',
      sublabel: 'Total enrolled',
      trend: 'neutral',
      trend_value: 0,
      trend_label: 'No change'
    },
    classes_today: {
      value: 0,
      label: 'Lessons Today',
      sublabel: 'Scheduled sessions',
      trend: 'neutral',
      trend_label: 'Same as last week'
    },
    revenue_mtd: {
      value: '$0',
      label: 'Revenue MTD',
      sublabel: 'Month to date',
      trend: 'neutral',
      trend_value: 0,
      trend_label: 'No change'
    },
    pending_payments: {
      value: 0,
      label: 'Pending Payments',
      sublabel: 'Due this week',
      trend: 'neutral',
      trend_label: 'Same as last week',
      amount: '$0'
    }
  };
  metricsLoading: boolean = true;
  private currentDateFormat = 'dddd, MMMM D, YYYY';

  ngOnInit(): void {
    this.loadDashboardData();
    this.loadTranslations();
    this.subscribeToStudioChanges();
    this.loadDashboardMetrics();
  }

  ngOnDestroy(): void {
    // Clean up subscription to prevent memory leaks
    if (this.studioChangeSubscription) {
      this.studioChangeSubscription.unsubscribe();
    }
    this.studioConfigSubscription?.unsubscribe();
  }

  // Studio config is app-lifetime; released so the dashboard can be collected
  // after navigating away.
  private studioConfigSubscription: Subscription;

  private subscribeToStudioChanges(): void {
    // Subscribe to studio changes and reload data when studio changes.
    // skip(1) drops the BehaviorSubject's replay of the current value. ngOnInit already loaded
    // the initial studio's data directly, so only act on *subsequent* studio changes.
    this.studioChangeSubscription = this._authService.selectedStudioChanged.pipe(skip(1)).subscribe((studio) => {
      if (studio && studio.id) {
        LoggerService.log('Dashboard: Studio changed to', studio.key || studio.name, '- reloading data');
        this.loadDashboardData();
        this.loadDashboardMetrics();
      }
    });
  }

  private loadDashboardData(): void {
    this.getDashboard();
    this.getCharts();
  }

  private loadDashboardMetrics(): void {
    const studioId = this._authService.selectedStudio?.id;
    
    if (!studioId) {
      LoggerService.log('Dashboard: No studio selected for metrics');
      this.metricsLoading = false;
      return;
    }

    this.metricsLoading = true;
    const endpoint = `/amt_dashboard/metrics/${studioId}`;
    
    this.drupalRESTService.httpGET(endpoint).subscribe({
      next: (response: any) => {
        if (response && response.success && response.data) {
          this.metrics = response.data;
          LoggerService.log('Dashboard metrics loaded:', this.metrics);
        }
        this.metricsLoading = false;
      },
      error: (error) => {
        LoggerService.error('Error loading dashboard metrics:', error);
        // Keep default values on error
        this.metricsLoading = false;
      }
    });
  }

  loadTranslations(): void {
    // Example of getting translation in component code
    this.translate.get('ROUTES.DASHBOARD').subscribe((res: string) => {
      this.pageTitle = res;
    });
  }

  getCharts() {
    this._utilityService.loadCharts()
      .subscribe(data => {
        this.chart_data = data;
      })
  }

  getDashboard() {
    this._utilityService.getDashboardPage()
      .subscribe(
        (data) => {
          this.dashboardData = data;
          LoggerService.log(this.dashboardData)
        })
  }

  // Methods for template binding
  getCurrentDate(): string {
    return moment().format(this.currentDateFormat);
  }

  getActiveStudentsCount(): number {
    return this.metrics?.active_students?.value || 0;
  }

  getTodayClassesCount(): number {
    return this.metrics?.classes_today?.value || 0;
  }

  getMonthlyRevenue(): string {
    return this.metrics?.revenue_mtd?.value || '$0';
  }

  getPendingPayments(): number {
    return this.metrics?.pending_payments?.value || 0;
  }

  onQuickActionClicked(actionName: string): void {
    // Log the action for analytics or tracking purposes
    LoggerService.log('Quick action clicked:', actionName);
  }

  refreshMetrics(): void {
    // Refresh all dashboard data
    this.loadDashboardData();
    this.loadDashboardMetrics();
    
    this.snackBar.open('Dashboard refreshed', 'Close', {
      duration: 2000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  get canViewStudentMetrics(): boolean {
    return this._permissionsService.canAccess('students_view');
  }

  get canViewCalendarMetrics(): boolean {
    return this._permissionsService.canAccess('calendar_view');
  }

  get canViewPaymentMetrics(): boolean {
    return this._permissionsService.canAccess('payments_view');
  }

  get canViewAnyMetric(): boolean {
    return this.canViewReportsSection
      && (this.canViewStudentMetrics || this.canViewCalendarMetrics || this.canViewPaymentMetrics);
  }

  get canViewReportsSection(): boolean {
    return this._permissionsService.canAccess('reports_view') || this._permissionsService.canAccess('reports_ami');
  }

  get canViewActivity(): boolean {
    return this._permissionsService.canAccess('students_view')
      || this._permissionsService.canAccess('enrollments_view')
      || this._permissionsService.canAccess('payments_view')
      || this._permissionsService.canAccess('calendar_view');
  }
}
